import { ItsLoggerEvent, RawPageData, ITSEventType } from 'its-pages';

const lokiParams = {
  url: '',
  src: '',
  bugReportSrc: ''
}

export function setLokiParams(params:typeof lokiParams): void{
  Object.assign(lokiParams, params);
}

type LokiStatus = 'success';
/*
  0: time stamp
  1: value
*/
type LokiValue = string[];
interface LokiResult {
  stream: Record<string, string>;
  values: LokiValue[];
}
interface LokiData{
  resultType: string;
  result: LokiResult[];
}
interface LokiLogResponse {
  status: LokiStatus;
  data: LokiData;
  limit: string;
}

const getJson = async <Q extends Record<string, string>, R>(url: string, parameters: Q): Promise<R> => {

  const searchParams = new URLSearchParams(parameters);
  try {
    url = `${url}?${searchParams}`;
    const response = await fetch(url);
    if (response.ok) return await response.json();
  } catch (e) {
    throw new Error(`network error ${JSON.stringify(e, null, 2)}`);
  }
  throw new Error('network error');
};

interface GetEventReportsParams {
  // url: string,
  start: bigint
  end?: bigint,
  cid?: string,
  uid?: string,
  skillName?: string,
  submit?: boolean
}

/**
 * Maximum number of results returned by loki.
 * Capped at 5000 by loki itself.
 */
const MAX_RESULT_COUNT = '100';

const getLokiLineLimit = (cid: string | undefined) => {
  return (globalThis as any)['its_loki_line_limit'] ?? MAX_RESULT_COUNT;
}

const lokiTimeShift = 1000000n;
const timeShiftDown = (t:bigint):number=>Number(t/lokiTimeShift);
const timeShiftUp = (t:number)=>(BigInt(t)*lokiTimeShift);
async function getEventReports(params: GetEventReportsParams, srcLabel?: string
  ): Promise<[ItsLoggerEvent[], bigint]> {
  let end = (params.end) ? params.end : timeShiftUp(Date.now());
  const { start, cid, uid, skillName: skill, submit=true } = params;
  const useDefaultSrcLabel = srcLabel === undefined;
  const lokiSrc = useDefaultSrcLabel ? lokiParams.src : srcLabel;
  const parameters = {
    query:`{src="${lokiSrc}"${useDefaultSrcLabel ? `,submit_type="${submit}"` : ''}${cid ? `,cid="${cid}"`: ''}}`,
    start:start.toString(),
    end:end.toString(), 
    direction:'forward',
    limit: getLokiLineLimit(cid)
  }
  try {
    const response = await getJson<typeof parameters, LokiLogResponse>(
      lokiParams.url,
      parameters
    );

    const results = response?.data?.result?.reduce((ac: LokiValue[], cv: LokiResult): LokiValue[]  => {
      return [...ac, ...cv.values]
    }, [] as LokiValue[]);

    const ret:ItsLoggerEvent[] = [];

    results?.forEach(element => {
      let event: ItsLoggerEvent | null;
      try { 
        event = {...JSON.parse(element[1]), "timestamp":timeShiftDown(BigInt(element[0])), id:element[0]};
        if (uid && (uid !== event?.uid)) {
          event = null;
        }
        else if (skill && (skill !== (event?.eventData as RawPageData)?.skill.id)) {
          event = null;
        }
        if (event) {
          ret.push(event)
        };
        } catch {
          return;
      }
    });
    if (results?.length) {
      end = BigInt(results[results.length-1][0]);
    }
    return ([ret, end]);
  } catch {
    return [[], end];
  }
}

export interface GetProblemResultsParams {
  cid: string,
  start: number,
  end?: number,
  uid?: string,
  skillName?: string,
}
export async function getProblemResults(params: GetProblemResultsParams): Promise<ItsLoggerEvent[]> {
  const { start: startParam, end: endParam, cid, uid, skillName } = params;
  const start = timeShiftUp(startParam);
  const end = endParam && timeShiftUp(endParam) || undefined;

  const [ret] = await getEventReports({start, end, cid, uid, skillName});
  return ret ;
}

interface GetBugReportsParams {
  start: number,
  end: number,
}
export async function getBugReports(params: GetBugReportsParams): Promise<ItsLoggerEvent[]> {
  const { start: startParam, end: endParam } = params;
  const start = timeShiftUp(startParam);
  const end = endParam && timeShiftUp(endParam) || undefined;

  const [events] = await getEventReports({start, end, submit:false}, lokiParams.bugReportSrc);

  const ret = events.filter((element)=> {
    const isBug = element.eventType  === ITSEventType.bugReport;

    return isBug;
  })
  return ret;
}

export async function getProblemResult(params: {time: string}): Promise<ItsLoggerEvent | undefined> {
  const time = BigInt(params.time);
  const [ret] = await getEventReports({start: time, end: time, submit:true});

  return ret[0];
}
